import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

// Lucide ships one component per glyph, so a tab names the icon it wants and the template
// picks the component — a class string can't select an svg the way it selected a webfont glyph.
export type TabIcon = 'shield' | 'cloud-upload'

export interface TabConfig {
  label: string
  route: string
  icon?: TabIcon
  loading?: boolean
}

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabsComponent implements OnDestroy {
  @Input() tabs: TabConfig[] = []
  @Input() baseRoute: string = ''
  @Input() activeTabIndex: number = 0
  @Input() useRouting: boolean = true
  @Input() scrollTargetId?: string
  @Output() tabChange = new EventEmitter<number>()

  private destroy$ = new Subject<void>()

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Listen for navigation end events to scroll to content
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        // OnPush components don't re-evaluate isActive() on router URL changes triggered
        // from outside this component (e.g. a pill routerLink elsewhere on the page), so
        // we must mark for check to keep the active tab indicator in sync with the URL.
        if (this.useRouting) {
          this.cdr.markForCheck()
        }

        if (this.useRouting && this.scrollTargetId) {
          // Use setTimeout to ensure DOM is updated after route change
          setTimeout(() => {
            const element = document.getElementById(this.scrollTargetId)
            if (element) {
              // Calculate offset for sticky header and tabs
              const headerElement = document.querySelector('.header-container')
              const tabsElement = document.querySelector('app-tabs .tabs-container')

              let offset = 0
              if (headerElement) {
                offset += headerElement.getBoundingClientRect().height
              }
              if (tabsElement) {
                offset += tabsElement.getBoundingClientRect().height
              }

              // Get element position and scroll with offset
              const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
              window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth',
              })
            }
          }, 0)
        }
      })
  }

  isActive(tab: TabConfig): boolean {
    if (!this.useRouting) {
      const tabIndex = this.tabs.indexOf(tab)
      return tabIndex === this.activeTabIndex
    }

    // Strip query parameters from URL for comparison
    const currentUrl = this.router.url.split('?')[0]
    const fullRoute = this.getFullRoute(tab)

    // Handle empty route as the default/index tab
    if (tab.route === '') {
      // Active if we're at the base route exactly
      return currentUrl === this.baseRoute || currentUrl === `${this.baseRoute}/`
    }

    // Active if current URL starts with the full route
    return currentUrl.startsWith(fullRoute)
  }

  navigateToTab(tab: TabConfig): void {
    if (!this.useRouting) {
      const tabIndex = this.tabs.indexOf(tab)
      this.tabChange.emit(tabIndex)
      return
    }

    const fullRoute = this.getFullRoute(tab)
    this.router.navigateByUrl(fullRoute)
  }

  private getFullRoute(tab: TabConfig): string {
    if (tab.route === '') {
      return this.baseRoute
    }
    return `${this.baseRoute}/${tab.route}`
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
